import {
  attribute,
  cos,
  float,
  floor,
  hash,
  instanceIndex,
  mix,
  sin,
  smoothstep,
  uniform,
  vec3,
  vec4,
} from 'three/tsl';
import * as THREE from 'three/webgpu';

type N = any;

export interface BirdDef {
  flocks: number;
  perFlock: number;
  /** Flight height above the camera's ground level (m): [min, max]. */
  altitude: [number, number];
  /** Circle radius range (m). */
  radius: [number, number];
  /** Flight speed (m/s). */
  speed: number;
  /** Wingspan (m). */
  span: number;
  color: [number, number, number];
  /** Optional visibility multiplier (e.g. day only). */
  visible?: () => N;
}

const CELL = 520; // flocks repeat on a lattice this size around the camera

/**
 * Small flocks of birds circling over the land. Each flock orbits a point fixed in
 * the hollow's frame (wrapped on a lattice around the camera), so walking under a
 * flock passes it by instead of dragging it along. Birds are dark silhouettes with
 * flapping wings, drawn as one instanced mesh.
 */
export class Birds {
  readonly mesh: THREE.Mesh;
  private readonly time = uniform(0);
  private readonly camLocal = uniform(new THREE.Vector3());
  private readonly up = uniform(new THREE.Vector3(0, 1, 0));
  private readonly t1 = uniform(new THREE.Vector3(1, 0, 0));
  private readonly t2 = uniform(new THREE.Vector3(0, 0, 1));

  constructor(def: BirdDef) {
    // One bird: a body point and two wings (tips flap), in local (x across, y up, z along).
    const geo = new THREE.BufferGeometry();
    const s = def.span / 2;
    // prettier-ignore
    const pos = new Float32Array([
      0,
      0,
      0.35,
      -s,
      0,
      -0.1,
      0,
      0,
      -0.25, // left wing
      0,
      0,
      0.35,
      0,
      0,
      -0.25,
      s,
      0,
      -0.1, // right wing
    ]);
    const tip = new Float32Array([0, 1, 0, 0, 0, 1]);
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    geo.setAttribute('aTip', new THREE.BufferAttribute(tip, 1));

    const mat = new THREE.MeshBasicNodeMaterial({ side: THREE.DoubleSide });
    const n = def.flocks * def.perFlock;
    const i = float(instanceIndex);
    const flock = floor(i.div(def.perFlock));
    const h = (k: number) => hash(flock.mul(31).add(k));
    const hb = (k: number) => hash(i.mul(17).add(k + 500));
    const t = this.time;

    // Flock centre: a fixed point in the hollow frame, wrapped to the camera's cell.
    const base = vec3(h(1), h(2), h(3)).mul(CELL);
    const rel = base.sub(this.camLocal);
    const wrapped = rel.sub(floor(rel.add(CELL / 2).div(CELL)).mul(CELL));
    // Project onto the local tangent plane and lift to flight height.
    const along1 = wrapped.dot(this.t1);
    const along2 = wrapped.dot(this.t2);
    const alt = mix(float(def.altitude[0]), float(def.altitude[1]), h(4));
    const radius = mix(float(def.radius[0]), float(def.radius[1]), h(5));
    const dirSign = h(6).greaterThan(0.5).select(float(1), float(-1));
    const angSpeed = float(def.speed).div(radius).mul(dirSign);
    // Each bird trails the leader in a loose line with its own bob.
    const lag = hb(1)
      .mul(0.35)
      .add(float(i.sub(flock.mul(def.perFlock))).mul(0.05));
    const ang = t.mul(angSpeed).add(h(7).mul(6.283)).sub(lag.mul(dirSign));
    const r = radius.add(hb(2).sub(0.5).mul(14));
    const bob = sin(t.mul(hb(3).mul(0.8).add(0.6)).add(hb(4).mul(6.28))).mul(2.5);
    const center = this.t1
      .mul(along1)
      .add(this.t2.mul(along2))
      .add(this.up.mul(alt.add(hb(5).sub(0.5).mul(10))));
    const posRel = center
      .add(this.t1.mul(cos(ang).mul(r)))
      .add(this.t2.mul(sin(ang).mul(r)))
      .add(this.up.mul(bob));
    // Heading: tangent of the circle.
    const fwd = this.t1
      .mul(sin(ang).negate())
      .add(this.t2.mul(cos(ang)))
      .mul(dirSign);
    const side = fwd.cross(this.up);
    const local = attribute('position', 'vec3');
    const tipW = attribute('aTip', 'float');
    const flap = sin(t.mul(hb(6).mul(3).add(9)).add(hb(7).mul(6.28)))
      .mul(def.span * 0.35)
      .mul(tipW);
    // Birds far from the camera fade out before they wrap to the other side of the cell.
    const dist = posRel.length();
    const fade = float(1).sub(smoothstep(CELL * 0.35, CELL * 0.48, dist));
    let scale: N = fade;
    if (def.visible) scale = scale.mul(def.visible());
    mat.positionNode = posRel
      .add(side.mul(local.x.mul(scale)))
      .add(this.up.mul(local.y.add(flap).mul(scale)))
      .add(fwd.mul(local.z.mul(scale)));
    mat.colorNode = vec4(vec3(...def.color), 1);

    this.mesh = new THREE.Mesh(geo, mat);
    this.mesh.count = n;
    this.mesh.frustumCulled = false;
    this.mesh.name = 'birds';
  }

  /** camLocal: camera position in the hollow's frame. */
  update(camLocal: THREE.Vector3, dt: number): void {
    this.time.value += dt;
    this.camLocal.value.copy(camLocal);
    const up = this.up.value.copy(camLocal).normalize().negate();
    const helper = Math.abs(up.y) < 0.9 ? _y : _x;
    this.t1.value.crossVectors(helper, up).normalize();
    this.t2.value.crossVectors(up, this.t1.value).normalize();
  }
}

const _x = new THREE.Vector3(1, 0, 0);
const _y = new THREE.Vector3(0, 1, 0);
